import * as React from 'react';
import {
  ArrowSquareOut,
  Copy,
  GitBranch,
  GithubLogo,
  GitPullRequest,
  Info,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { Checkbox, Table } from '@fragments-sdk/ui';
import {
  createColumnHelper,
  flexRender,
  type Row,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import type { FindingDoc } from './findings-types';
import {
  buildGitHubUrl,
  canonicalSeverity,
  classNames,
  disambiguatedPath,
  extractRepoFromBinding,
  findingFixContext,
  findingLifecycleLabel,
  findingStatusLabel,
  formatCategoryLabel,
  formatRuleIdentity,
  isColorValue,
  parseDuplicateMessage,
  parseFindingAttributes,
  resolveSuggestion,
  severityLabel,
  stripCategoryPrefix,
} from './findings-display-model';
import { FindingActionsCell } from './finding-actions-cell';
import { useFindingsRowStatic } from './findings-table-context';
import {
  formatGovernanceCodeLabel,
  formatGovernanceCodeTitle,
} from '@/lib/govern-code-catalog';
import s from './findings.module.scss';

export {
  FindingsActionContext,
  FindingsRowStaticContext,
  type FindingsActionContextValue,
  type FindingsRowStaticContextValue,
} from './findings-table-context';

const TH_CLASSES: Record<string, string | undefined> = {
  select: s.thSelect,
  attention: s.thAttention,
  message: s.thMessage,
  fix: s.thFix,
  actions: s.thActions,
};

const TD_CLASSES: Record<string, string | undefined> = {
  select: s.tdSelect,
  attention: s.tdAttention,
  message: s.tdMessage,
  fix: s.tdFix,
  actions: s.tdActions,
};

export function thClass(colId: string): string {
  return classNames(s.findingsTableHeaderCell, TH_CLASSES[colId]);
}

function tdClass(colId: string): string {
  return classNames(s.findingsTableCell, TD_CLASSES[colId]);
}

export const FindingsTableRow = React.memo(
  function FindingsTableRow({
    row,
    selected,
    rowRef,
    dataIndex,
    rowBand,
  }: {
    row: Row<FindingDoc>;
    selected: boolean;
    rowRef?: (node: HTMLTableRowElement | null) => void;
    dataIndex?: number;
    rowBand?: 'default' | 'alt';
  }) {
    return (
      <Table.Row
        ref={rowRef}
        band={rowBand}
        selected={selected}
        data-index={dataIndex}
        data-severity={row.original.severity}
      >
        {row.getVisibleCells().map((cell) => (
          <Table.Cell key={cell.id} className={tdClass(cell.column.id)}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Table.Cell>
        ))}
      </Table.Row>
    );
  },
  (prev, next) =>
    prev.row.id === next.row.id &&
    prev.row.original === next.row.original &&
    prev.selected === next.selected &&
    prev.dataIndex === next.dataIndex &&
    prev.rowBand === next.rowBand,
);

function SelectAllHeaderCell({ table }: { table: TanstackTable<FindingDoc> }) {
  const { readOnly } = useFindingsRowStatic();
  const isAll = table.getIsAllRowsSelected();
  const isSome = table.getIsSomeRowsSelected();
  return (
    <Checkbox
      size="sm"
      className={s.checkboxControl}
      disabled={readOnly}
      checked={isAll}
      indeterminate={!isAll && isSome}
      onCheckedChange={(checked) => table.toggleAllRowsSelected(checked)}
      aria-label={
        readOnly
          ? 'Selection disabled in read-only demo'
          : isAll
            ? 'Deselect all'
            : 'Select all'
      }
    />
  );
}

function SelectRowCell({
  row,
  table: tbl,
}: {
  row: Row<FindingDoc>;
  table: TanstackTable<FindingDoc>;
}) {
  const {
    readOnly,
    selection: {
      setRowSelection,
      lastClickedIndexRef,
      skipNextCheckedChangeRef,
    },
  } = useFindingsRowStatic();
  return (
    <Checkbox
      size="sm"
      className={s.checkboxControl}
      disabled={readOnly}
      checked={row.getIsSelected()}
      onClick={(e) => {
        if (readOnly) return;
        if (e.shiftKey && lastClickedIndexRef.current != null) {
          e.preventDefault();
          e.stopPropagation();
          skipNextCheckedChangeRef.current = true;
          const start = Math.min(lastClickedIndexRef.current, row.index);
          const end = Math.max(lastClickedIndexRef.current, row.index);
          const allRows = tbl.getRowModel().rows;
          setRowSelection((prev) => {
            const next = { ...prev };
            for (let i = start; i <= end; i++) {
              if (allRows[i]) next[allRows[i].id] = true;
            }
            return next;
          });
          lastClickedIndexRef.current = row.index;
        }
      }}
      onCheckedChange={(checked) => {
        if (readOnly) return;
        if (skipNextCheckedChangeRef.current) {
          skipNextCheckedChangeRef.current = false;
          return;
        }
        row.toggleSelected(checked);
        lastClickedIndexRef.current = row.index;
      }}
      aria-label={
        readOnly
          ? 'Selection disabled in read-only demo'
          : row.getIsSelected()
            ? 'Deselect row'
            : 'Select row'
      }
    />
  );
}

function FindingSeverityMark({ severity }: { severity: string }) {
  const canonical = canonicalSeverity(severity);
  const SeverityIcon =
    canonical === 'critical' || canonical === 'serious'
      ? XCircle
      : canonical === 'moderate'
        ? Warning
        : Info;
  const label = `${severityLabel(severity)} severity`;

  return (
    <span
      className={s.findingSeverityMark}
      data-severity={canonical}
      title={label}
      aria-label={label}
    >
      <SeverityIcon size={11} weight="fill" />
    </span>
  );
}

const columnHelper = createColumnHelper<FindingDoc>();

export const FINDINGS_COLUMNS = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => <SelectAllHeaderCell table={table} />,
    cell: ({ row, table: tbl }) => <SelectRowCell row={row} table={tbl} />,
    size: 36,
  }),
  columnHelper.display({
    id: 'attention',
    header: () => null,
    cell: ({ row }) => <FindingSeverityMark severity={row.original.severity} />,
    size: 32,
  }),
  columnHelper.display({
    id: 'message',
    header: () => 'Finding',
    cell: ({ row }) => <FindingMessageCell finding={row.original} />,
  }),
  columnHelper.display({
    id: 'fix',
    header: () => 'Suggested fix',
    cell: ({ row }) => <FindingFixCell finding={row.original} />,
    size: 300,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => null,
    cell: ({ row }) => <FindingActionsCell finding={row.original} />,
    size: 112,
  }),
];

function FindingCodeCell({ finding }: { finding: FindingDoc }) {
  const label = formatGovernanceCodeLabel(finding);
  const title = formatGovernanceCodeTitle(finding);
  const isUncoded = label === 'Uncoded';

  return (
    <span
      className={s.findingCodePill}
      data-empty={isUncoded || undefined}
      title={title}
    >
      {label}
    </span>
  );
}

function FindingMessageCell({ finding }: { finding: FindingDoc }) {
  const { groupBy } = useFindingsRowStatic();
  const dupParts =
    finding.ruleId === 'tokens/duplicate-value'
      ? parseDuplicateMessage(finding.message)
      : null;
  const categoryLabel = finding.category
    ? formatCategoryLabel(finding.category)
    : null;
  const ruleIdentity = formatRuleIdentity(finding);
  const resolutionSource = tailwindResolutionSource(finding);
  const lifecycle =
    finding.status === 'open' ? null : findingStatusLabel(finding.status);
  // Product area (topology layer), criticality rendered as a plain label — color
  // stays reserved for severity. Only shown when grouping doesn't already key
  // on area, to avoid repeating the group header on every row.
  const areaLabel =
    groupBy !== 'area' && finding.areaName
      ? `Area: ${finding.areaName}${
          finding.areaCriticality ? ` (${finding.areaCriticality})` : ''
        }`
      : null;
  const metaItems =
    groupBy === 'rule'
      ? [
          `Severity: ${severityLabel(finding.severity)}`,
          lifecycle,
          categoryLabel,
          finding.prop,
          resolutionSource,
          areaLabel,
        ].filter(Boolean)
      : [
          `Severity: ${severityLabel(finding.severity)}`,
          lifecycle,
          `Rule: ${ruleIdentity}`,
          finding.prop,
          resolutionSource,
          areaLabel,
        ].filter(Boolean);

  return (
    <div className={s.findingSummary}>
      <div className={s.findingTitleLine}>
        <FindingCodeCell finding={finding} />
        <div className={s.findingMessage}>
          {dupParts
            ? `${dupParts.tokenNames.length} tokens share the same value`
            : stripCategoryPrefix(finding.message)}
        </div>
      </div>
      <div className={s.findingMetaLine}>
        {finding.filePath && (
          <span className={s.findingLocationMeta}>
            <FindingFileCell finding={finding} />
          </span>
        )}
        {metaItems.map((item) => (
          <span key={item}>{item}</span>
        ))}
        <FindingSourceChip finding={finding} />
      </div>
    </div>
  );
}

function tailwindResolutionSource(finding: FindingDoc): string | null {
  if (!finding.ruleId.startsWith('tailwind/')) return null;
  const attributes = parseFindingAttributes(finding);
  const source = attributes.source;
  return typeof source === 'string' && source.length > 0
    ? `Resolved: ${source}`
    : null;
}

function FindingFixCell({ finding }: { finding: FindingDoc }) {
  const { copy: onCopy } = useFindingsRowStatic();
  const sg = resolveSuggestion(finding);
  const context = findingFixContext(finding);
  const contextNode = renderFixContext(context);
  const dupParts =
    finding.ruleId === 'tokens/duplicate-value'
      ? parseDuplicateMessage(finding.message)
      : null;

  if (dupParts) {
    return (
      <div className={s.fixCell}>
        <span className={s.replacementPair}>
          {isColorValue(dupParts.value) && (
            <span
              className={s.colorSwatch}
              style={{ backgroundColor: dupParts.value }}
            />
          )}
          <span className={s.rawValueCode}>{dupParts.value}</span>
          <span className={s.dupTokenList}>
            {dupParts.tokenNames.map((name) => (
              <span
                key={name}
                className={s.tokenCode}
                role="button"
                tabIndex={0}
                title="Click to copy"
                onClick={() => onCopy(`var(${name})`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onCopy(`var(${name})`);
                }}
              >
                {name}
              </span>
            ))}
          </span>
        </span>
        {sg?.kind === 'prose' && (
          <span className={s.proseSuggestion}>{sg.display}</span>
        )}
        {contextNode}
      </div>
    );
  }

  if (finding.rawValue && sg?.kind === 'token') {
    return (
      <div className={s.fixCell}>
        <span className={s.replacementPair}>
          {isColorValue(finding.rawValue) && (
            <span
              className={s.colorSwatch}
              style={{ backgroundColor: finding.rawValue }}
            />
          )}
          <span className={s.rawValueCode}>{finding.rawValue}</span>
          <span className={s.replacementArrow}>&rarr;</span>
          <span
            className={s.tokenCode}
            role="button"
            tabIndex={0}
            title="Click to copy"
            onClick={() => sg.copyText && onCopy(sg.copyText)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && sg.copyText) onCopy(sg.copyText);
            }}
          >
            {sg.display}
            <Copy size={9} className={s.copyIcon} />
          </span>
        </span>
        {contextNode}
      </div>
    );
  }

  if (sg?.kind === 'prose') {
    return (
      <span className={s.fixCell}>
        <span className={s.proseSuggestion}>{sg.display}</span>
        {contextNode}
      </span>
    );
  }

  if (sg?.kind === 'token') {
    return (
      <span className={s.fixCell}>
        <span
          className={s.suggestion}
          role="button"
          tabIndex={0}
          title="Click to copy"
          onClick={() => sg.copyText && onCopy(sg.copyText)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && sg.copyText) onCopy(sg.copyText);
          }}
        >
          {sg.display}
          <Copy size={9} className={s.copyIcon} />
        </span>
        {contextNode}
      </span>
    );
  }

  return (
    <span className={s.fixCell}>
      <span className={s.noFixText}>Manual review</span>
      {contextNode}
    </span>
  );
}

function renderFixContext(context: string | null) {
  if (!context) return null;
  return (
    <span className={s.fixContext}>
      <span>Context</span>
      {context}
    </span>
  );
}

function FindingFileCell({ finding }: { finding: FindingDoc }) {
  const {
    copy: onCopy,
    groupBy,
    repoFullName: projectRepoFullName,
    repoRef,
  } = useFindingsRowStatic();
  const repoFullName = extractRepoFromBinding(finding) ?? projectRepoFullName;
  const ghUrl = finding.filePath
    ? buildGitHubUrl(
        repoFullName,
        finding.commitSha ?? finding.branch ?? repoRef,
        finding.filePath,
        finding.line ?? undefined,
      )
    : null;
  const displayPath = finding.filePath
    ? disambiguatedPath(finding.filePath)
    : null;

  if (groupBy === 'file') {
    const lineLabel = finding.line != null ? `:${finding.line}` : '-';
    return ghUrl ? (
      <a
        href={ghUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={s.lineLink}
        title={finding.filePath}
      >
        {lineLabel}
      </a>
    ) : (
      <span className={s.lineNum} title={finding.filePath}>
        {lineLabel}
      </span>
    );
  }

  return (
    <>
      {finding.filePath &&
        (ghUrl ? (
          <a
            href={ghUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={s.fileLink}
            title={finding.filePath}
          >
            {displayPath}
            <ArrowSquareOut size={9} />
          </a>
        ) : (
          <span
            className={s.filePath}
            role="button"
            tabIndex={0}
            title={`${finding.filePath} — click to copy`}
            onClick={() => onCopy(finding.filePath!)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCopy(finding.filePath!);
            }}
          >
            {displayPath}
          </span>
        ))}
      {finding.line != null && (
        <span className={s.lineNum}>:{finding.line}</span>
      )}
    </>
  );
}

function FindingSourceChip({ finding }: { finding: FindingDoc }) {
  const lifecycleLabel = findingLifecycleLabel(finding.status);

  return (
    <span className={s.findingSourceMeta}>
      {finding.source === 'ci' && finding.prNumber ? (
        <>
          <GitPullRequest size={10} /> PR #{finding.prNumber}
        </>
      ) : finding.source === 'ci' && finding.commitSha ? (
        <>
          <GitBranch size={10} /> {finding.commitSha.slice(0, 7)}
        </>
      ) : finding.source === 'ci' ? (
        'CI / CD'
      ) : finding.source === 'manual' ? (
        'Manual'
      ) : (
        'Repo scan'
      )}
      <span data-status={finding.status}>{lifecycleLabel}</span>
      {finding.githubIssueUrl && (
        <a
          href={finding.githubIssueUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={s.sourceLink}
        >
          <GithubLogo size={10} />
          Issue
        </a>
      )}
    </span>
  );
}
